/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Michael Diponio
 * Created: 26/04/2022
 */

import * as React from 'react';
import Page from '@/wpt-lib/pdf/common/Page';
import { Cell, HeadingCell, Row, Table, TitleCell, ValueCell } from '@/wpt-lib/pdf/common/Table';
import useData from '@/wpt-lib/pdf/useData';
import Allocation from '@/wpt-lib/pdf/charts/Allocation';
import {
    mapWorkloadStaffTypeToString,
    mapWorkplanStateToString,
    WorkloadStaffType,
} from '@/wpt-lib/interfaces/Workplan';
import { formatFaculty, formatName } from '@/wpt-lib/utils/formatName';

const Summary = ({ charts }: { charts: JSX.Element[] }) => {
    const { user, version, workplan } = useData();

    return (
        <Page>
            <Table cols={5} border>
                <Row>
                    <TitleCell
                        title={
                            `UTS Workplan ${workplan.year} - Version ${version.version}` +
                            (version.version !== workplan.currentVersion.version
                                ? ' - Superseded'
                                : ` - ${mapWorkplanStateToString[version.state] ?? version.state}`)
                        }
                        variant="summary"
                    />
                </Row>
                <Row>
                    <HeadingCell
                        heading={`${mapWorkloadStaffTypeToString[version.staffType ?? WorkloadStaffType.ACADEMIC]} ${
                            version.staffType !== WorkloadStaffType.SENIOR ? 'Staff ' : ''
                        }Member`}
                        top={2}
                    />
                </Row>
                <Row>
                    <ValueCell label="Name" value={formatName(user)} textAlign="center" />
                    <ValueCell
                        label="Faculty/School"
                        value={`${formatFaculty(workplan.assignment?.organisation.faculty)} ${
                            workplan.assignment?.organisation.name
                        }`}
                        textAlign="center"
                    />
                    <ValueCell label="Staff Number" value={user.staffId} textAlign="center" />
                    <ValueCell
                        label="My Supervisor"
                        value={version.supervisors
                            ?.filter((supervisor) => supervisor.isAutoAssigned)
                            .map((s) => formatName(s.user))
                            .join(' ')}
                        textAlign="center"
                    />
                    <ValueCell
                        label="Alternate Supervisors"
                        value={version.supervisors
                            ?.filter((supervisor) => !supervisor.isAutoAssigned)
                            .map((s) => formatName(s.user))
                            .join(', ')}
                        textAlign="center"
                    />
                </Row>
                <Row widths={[12, 21.32, 66.66]}>
                    <ValueCell label="Workplan Year" value={workplan.year} textAlign="center" />
                    <ValueCell
                        label="Assignment"
                        value={`${workplan.assignment?.assignmentNumber} - ${workplan.assignment?.name}`}
                    />
                    <HeadingCell heading="Staff Workload Allocation" top={13} />
                </Row>
                <Row>
                    <HeadingCell heading={version.probationPlan ? 'Probation Workplan' : 'Annual Workplan'} />
                    <ValueCell
                        label="Teaching"
                        value={Math.round(version.teachingWorkloadPortion * 100)}
                        textAlign="center"
                    />
                    <ValueCell
                        label="Research"
                        value={Math.round(version.researchWorkloadPortion * 100)}
                        textAlign="center"
                    />
                    <ValueCell
                        label="Other/External Engagement"
                        value={Math.round(version.serviceWorkloadPortion * 100)}
                        textAlign="center"
                    />
                    {version.staffType === WorkloadStaffType.PROFESSIONAL && (
                        <ValueCell
                            label="Professional"
                            value={Math.round(version.professionalStaffWorkloadPortion * 100)}
                            textAlign="center"
                        />
                    )}
                    {version.staffType === WorkloadStaffType.SENIOR && (
                        <ValueCell
                            label="Senior Staff Group"
                            value={Math.round(version.seniorStaffWorkloadPortion * 100)}
                            textAlign="center"
                        />
                    )}
                </Row>
                <Row>
                    <Cell>
                        <Allocation image={charts[0]} />
                    </Cell>
                </Row>
            </Table>
        </Page>
    );
};

export default Summary;
